'use client'

import ChatMessageItem from '@/components/ChatV2/ChatMessageItem'
import { useScrollbarVisibility } from '@/hooks/useScrollbarVisibility'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { ChatItemV2, ChatItemV2System } from '@/lib/type'
import { cn } from '@/lib/utils'
import RiveComponent, { Fit, Layout } from '@rive-app/react-canvas'
import { useVirtualizer } from '@tanstack/react-virtual'
import { AnimatePresence, motion } from 'framer-motion'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FiChevronDown } from 'react-icons/fi'

// Constants for spacing and padding values
const CONTAINER_TOP_PADDING_PX = 80 // pt-20 top padding for header
const USER_MESSAGE_PADDING_PX = 30 // User message padding/spacing
const MESSAGE_SPACING_PX = 15 // Message spacing

// Helper function to determine if tight spacing should be used between messages
function shouldUseTightSpacing(currentMessage: ChatItemV2, previousMessage?: ChatItemV2): boolean {
  if (!previousMessage) return false

  const { role: currentRole } = currentMessage
  const { role: previousRole } = previousMessage

  return (
    (currentRole === 'assistant' && previousRole === 'assistant') ||
    (currentRole === 'tool' && ['assistant', 'tool'].includes(previousRole)) ||
    (currentRole === 'assistant' && previousRole === 'tool')
  )
}

type MessageBlock = {
  id: string
  messages: ChatItemV2[]
  isNewest: boolean
}

type ChatDisplayProps = {
  className?: string
}

export type ChatDisplayRef = {
  scrollToOptimalPosition: () => void
  updateSpacerHeight: () => void
  scrollToBottom: () => void
  getScrollContainer: () => HTMLElement | null
}

const ChatDisplay = forwardRef<ChatDisplayRef, ChatDisplayProps>(function ChatDisplay(
  { className = '' },
  ref
) {
  const { messages, status, hasHydrated, activeTypewriterMessageId, context, sessionId } =
    useCopilotContext()

  // We no longer track internal SSE status; rely on unified status from context

  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const chatListRef = useRef<HTMLDivElement>(null)
  const shouldScrollToBottomRef = useRef<boolean>(true)
  const initialMessageCountRef = useRef<number | null>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  // Cache for measured block sizes
  const blockSizeCacheRef = useRef<Map<string, number>>(new Map())
  const [averageBlockSize, setAverageBlockSize] = useState(400)

  // Group messages into blocks starting from each user message
  const messageBlocks = useMemo(() => {
    const blocks: MessageBlock[] = []
    let currentBlock: MessageBlock | null = null

    const initialBlockId = messages.length > 0 ? `initial-${messages[0].id}` : 'initial-root'

    for (const message of messages) {
      if (message.role === 'user') {
        // Start new block with user message
        currentBlock = {
          id: message.id,
          messages: [message],
          isNewest: false,
        }
        blocks.push(currentBlock)
      } else if (currentBlock) {
        // Add to current block
        currentBlock.messages.push(message)
      } else {
        // Create initial block for orphaned messages
        currentBlock = {
          id: initialBlockId,
          messages: [message],
          isNewest: false,
        }
        blocks.push(currentBlock)
      }
    }

    // Mark the last block as newest
    if (blocks.length > 0) {
      blocks[blocks.length - 1].isNewest = true
    }

    return blocks
  }, [messages])

  // Use the original messages array directly for virtualization
  const messagesToRender = messages

  // Find the last user message
  const lastMessage = messagesToRender[messagesToRender.length - 1]
  const lastUserMessage = [...messagesToRender].reverse().find((m) => m.role === 'user')
  const lastAssistantMessage = [...messagesToRender].reverse().find((m) => m.role === 'assistant')

  // Show thinking animation when sending or streaming and no active typewriter for last assistant message
  const showThinkingAnimation =
    (status === 'sending' || status === 'streaming') &&
    (!lastAssistantMessage || activeTypewriterMessageId !== lastAssistantMessage.id)

  // Add or remove thinking animation to the newest block based on current state
  const blocksToRender = useMemo(() => {
    const blocks = [...messageBlocks]

    if (blocks.length > 0) {
      const newestBlock = blocks[blocks.length - 1]
      const thinkingIndex = newestBlock.messages.findIndex(
        (msg) => msg.role === 'system' && msg.id === 'thinking-animation'
      )
      const hasThinking = thinkingIndex !== -1

      if (showThinkingAnimation && !hasThinking) {
        // Add thinking animation if needed and not present
        newestBlock.messages.push({
          id: 'thinking-animation',
          type: 'message',
          timestamp: '',
          role: 'system',
        } satisfies ChatItemV2System)
      } else if (!showThinkingAnimation && hasThinking) {
        // Remove thinking animation if not needed but present
        newestBlock.messages.splice(thinkingIndex, 1)
      }
    }

    return blocks
  }, [messageBlocks, showThinkingAnimation])

  // Debug logging for thinking animation
  useEffect(() => {
    if (showThinkingAnimation) {
      console.log('[Chat] 🤔 Thinking animation VISIBLE')
    } else {
      console.log('[Chat] ❌ Thinking animation HIDDEN')
    }
  }, [
    showThinkingAnimation,
    activeTypewriterMessageId,
    status,
    lastMessage,
    lastUserMessage,
    lastAssistantMessage,
    messagesToRender.length,
    // connectionStatus removed
    context.nodeExecutions,
  ])

  // Debug logging for status changes
  useEffect(() => {
    console.debug('[Chat] Status changed:', status)
  }, [status])

  // Cache-aware size estimation with fallback calculation
  const estimateBlockSize = useCallback(
    (index: number) => {
      const block = blocksToRender[index]
      if (!block) return averageBlockSize

      // Check cache first
      const cachedSize = blockSizeCacheRef.current.get(block.id)
      if (cachedSize) {
        return cachedSize
      }

      // Fallback to calculation for uncached blocks
      let estimatedHeight = 0

      for (const message of block.messages) {
        if (message.role === 'user') {
          // User messages: base height + content estimation
          const contentLength = message.content?.length || 0
          const hasAttachments = (message.context?.files?.length ?? 0) > 0

          const contentHeight = Math.max(40, contentLength * 0.4)
          estimatedHeight += 120 + contentHeight + (hasAttachments ? 80 : 0)
        } else if (message.role === 'assistant') {
          // Assistant messages: account for markdown formatting
          const contentLength = message.content.length || 0
          const content = message.content || ''

          let contentHeight = Math.max(40, contentLength * 0.5)

          // Extra height for code blocks, lists, and formatting
          const codeBlocks = (content.match(/```/g) || []).length / 2
          const lists = (content.match(/^[-*+]\s/gm) || []).length
          const newlines = (content.match(/\n/g) || []).length

          contentHeight += codeBlocks * 100
          contentHeight += lists * 25
          contentHeight += newlines * 20

          estimatedHeight += 100 + contentHeight
        } else if (message.role === 'tool') {
          estimatedHeight += 200
        } else if (message.role === 'system') {
          estimatedHeight += message.id === 'thinking-animation' ? 60 : 40
        }
      }

      // Add spacing between messages
      estimatedHeight += Math.max(0, block.messages.length - 1) * 20

      // Add minimal buffer to prevent overflow
      estimatedHeight = Math.floor(estimatedHeight * 1.02)

      return Math.max(50, estimatedHeight)
    },
    [blocksToRender, averageBlockSize]
  )

  // Function to cache measured block size
  const cacheBlockSize = useCallback((blockId: string, actualSize: number) => {
    blockSizeCacheRef.current.set(blockId, actualSize)

    // Update average block size for better estimates
    const sizes = Array.from(blockSizeCacheRef.current.values())
    if (sizes.length > 0) {
      const newAverage = sizes.reduce((sum, size) => sum + size, 0) / sizes.length
      setAverageBlockSize(Math.round(newAverage))
    }

    // Limit cache size to prevent memory issues
    if (blockSizeCacheRef.current.size > 100) {
      const keys = Array.from(blockSizeCacheRef.current.keys())
      if (keys.length > 0) {
        blockSizeCacheRef.current.delete(keys[0])
      }
    }
  }, [])

  // Virtualization setup with cache-aware size estimation
  const virtualizer = useVirtualizer({
    count: blocksToRender.length,
    getScrollElement: () => chatListRef.current,
    estimateSize: estimateBlockSize,
    overscan: 3,
  })

  const virtualBlocks = virtualizer.getVirtualItems()

  // Track ResizeObservers for each block to respond to late layout changes (e.g., images load)
  const blockResizeObserversRef = useRef<Map<string, ResizeObserver>>(new Map())

  // Disconnect all block ResizeObservers on unmount to prevent leaks
  useEffect(() => {
    return () => {
      blockResizeObserversRef.current.forEach((observer) => observer.disconnect())
      blockResizeObserversRef.current.clear()
    }
  }, [])

  // Calculate spacer height to position last user message at top of screen
  const calculateOptimalSpacerHeight = useCallback(() => {
    if (!chatListRef.current) return 0 // no fallback spacer

    const chatContainer = chatListRef.current
    const chatHeight = chatContainer.clientHeight

    // Find the newest block and the last user message in it
    const newestBlock = blocksToRender[blocksToRender.length - 1]
    if (!newestBlock) return 0

    // Find the last user message in the newest block
    const lastUserMessage = [...newestBlock.messages].reverse().find((msg) => msg.role === 'user')
    if (!lastUserMessage) return 0

    // Try to find the newest block element in the DOM
    const newestBlockElement = document.getElementById(`block-${newestBlock.id}`)
    if (!newestBlockElement) {
      // If not rendered yet, don't add spacer
      return 0
    }

    // Simple approach: calculate total height after user message and use reasonable buffer
    let totalHeightAfterUser = 0
    let foundUserMessage = false

    const messageElements = newestBlockElement.querySelectorAll('[aria-live="polite"]')
    messageElements.forEach((element) => {
      const messageElement = element as HTMLElement
      const isUserMessage = messageElement.querySelector('.flex-row-reverse') !== null

      if (isUserMessage) {
        foundUserMessage = true
        totalHeightAfterUser += USER_MESSAGE_PADDING_PX // User message padding/spacing
      } else if (foundUserMessage) {
        // AI response content - add full height plus spacing
        totalHeightAfterUser += messageElement.offsetHeight + MESSAGE_SPACING_PX // Message + spacing
      }
    })

    // Account for container vertical padding (top only)
    const containerPadding = CONTAINER_TOP_PADDING_PX
    const adjustedChatHeight = chatHeight - containerPadding

    // Simple calculation: spacer = container height - total content height
    const finalSpacer = Math.max(0, adjustedChatHeight - totalHeightAfterUser)

    return finalSpacer
  }, [blocksToRender, chatListRef])

  const defaultSpacerHeight = useMemo(
    () => calculateOptimalSpacerHeight(),
    [calculateOptimalSpacerHeight, blocksToRender]
  )

  // Calculate spacer height for the newest block (optimized to position last user message at top)
  const updateSpacerHeight = useCallback(() => {
    if (!chatListRef.current) return

    const newestBlock = blocksToRender[blocksToRender.length - 1]
    if (!newestBlock) return

    // Try to find the newest block element in the DOM
    const newestBlockElement = document.getElementById(`block-${newestBlock.id}`)
    if (!newestBlockElement) {
      // If newest block is virtualized out, don't auto-scroll - user might be scrolling up
      return
    }

    const spacerElement = newestBlockElement.querySelector(
      '[aria-hidden="true"]'
    ) as HTMLElement | null
    if (!spacerElement) return

    // Calculate optimal spacer height to position last user message at top
    const optimalSpacerHeight = calculateOptimalSpacerHeight()

    // Always set and re-measure to avoid stale layout and overlap
    spacerElement.style.height = `${optimalSpacerHeight}px`
    requestAnimationFrame(() => virtualizer.measure())
  }, [blocksToRender, virtualizer, calculateOptimalSpacerHeight])

  // Function to scroll to the bottom of the chat
  const scrollToBottom = useCallback((snap: boolean = false) => {
    if (chatListRef.current) {
      chatListRef.current.scrollTo({
        top: chatListRef.current.scrollHeight,
        behavior: snap ? 'auto' : 'smooth',
      })
      shouldScrollToBottomRef.current = true
      setShowScrollToBottom(false)
    }
  }, [])

  // Improved scroll function - positions last user message at top
  const scrollToOptimalPosition = useCallback(() => {
    if (blocksToRender.length === 0) return

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    // Check if the newest block is currently rendered
    const newestBlockIndex = blocksToRender.length - 1
    const newestBlock = blocksToRender[newestBlockIndex]

    if (newestBlock) {
      const newestBlockElement = document.getElementById(`block-${newestBlock.id}`)

      if (!newestBlockElement) {
        // Newest block is not rendered (virtualized out), scroll to bottom first
        scrollToBottom(true)
      }
    }

    // Use multiple requestAnimationFrame calls to ensure all content is rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Update spacer height first for accurate positioning
        updateSpacerHeight()

        // Then scroll to the newest (last) block using virtualizer
        const newestBlockIndex = blocksToRender.length - 1
        virtualizer.scrollToIndex(newestBlockIndex, {
          align: 'start',
          behavior: 'smooth', // smooth animated scroll for exact positioning
        })
      })
    })
  }, [blocksToRender, virtualizer, scrollToBottom, updateSpacerHeight])

  // Function to trigger spacer update during content changes (like typewriter, no auto-scroll)
  const triggerSpacerUpdate = useCallback(() => {
    // Only update spacer, don't trigger any scrolling
    requestAnimationFrame(() => {
      updateSpacerHeight()
    })
  }, [updateSpacerHeight])

  // Memoize virtual block keys to prevent unnecessary re-renders
  const virtualBlockKeys = useMemo(() => virtualBlocks.map((b) => b.key), [virtualBlocks])

  // Hook into react-virtual render cycle to recalculate spacer when blocks change (no auto-scroll)
  useEffect(() => {
    // When virtual blocks change (due to scrolling/virtualization), update spacer but don't auto-scroll
    if (virtualBlocks.length > 0) {
      // Use a slight delay to ensure DOM is updated
      requestAnimationFrame(() => {
        updateSpacerHeight()

        // Measure and cache actual block sizes
        const visibleBlockIds = new Set<string>()
        virtualBlocks.forEach((virtualBlock) => {
          const block = blocksToRender[virtualBlock.index]
          if (block) {
            visibleBlockIds.add(block.id)
            const blockElement = document.getElementById(`block-${block.id}`)
            if (blockElement) {
              const actualHeight = blockElement.offsetHeight
              const estimatedHeight = virtualBlock.size

              // Cache the actual size if we don't have it or if it's significantly different
              if (
                !blockSizeCacheRef.current.has(block.id) ||
                Math.abs(actualHeight - estimatedHeight) / estimatedHeight > 0.1
              ) {
                cacheBlockSize(block.id, actualHeight)

                // Trigger remeasure if size was significantly different
                if (Math.abs(actualHeight - estimatedHeight) / estimatedHeight > 0.3) {
                  requestAnimationFrame(() => virtualizer.measure())
                }
              }

              // Attach a ResizeObserver per block to keep measurements fresh
              if (!blockResizeObserversRef.current.get(block.id)) {
                const observer = new ResizeObserver((entries) => {
                  for (const entry of entries) {
                    const newHeight = Math.ceil(entry.contentRect.height)
                    const cached = blockSizeCacheRef.current.get(block.id)
                    if (!cached || Math.abs(newHeight - cached) > 2) {
                      cacheBlockSize(block.id, newHeight)
                      // Re-measure virtualization layout on next frame
                      requestAnimationFrame(() => virtualizer.measure())
                    }
                  }
                })
                observer.observe(blockElement)
                blockResizeObserversRef.current.set(block.id, observer)
              }
            }
          }
        })

        // Clean up observers for blocks that are no longer visible
        blockResizeObserversRef.current.forEach((observer, blockId) => {
          if (!visibleBlockIds.has(blockId)) {
            observer.disconnect()
            blockResizeObserversRef.current.delete(blockId)
          }
        })
      })
    }
  }, [
    virtualBlocks.length,
    virtualBlockKeys,
    updateSpacerHeight,
    virtualizer,
    blocksToRender,
    cacheBlockSize,
  ])

  // Handle scrollbar visibility on scroll
  useScrollbarVisibility(chatListRef)

  // Debounced scroll handler for better performance
  const handleScroll = useCallback(() => {
    const chatList = chatListRef.current
    if (!chatList) return

    // If user is within 50px of the bottom, consider them at the bottom
    const threshold = 50
    const scrollTop = chatList.scrollTop
    const scrollHeight = chatList.scrollHeight
    const clientHeight = chatList.clientHeight

    const atBottom = scrollHeight - scrollTop - clientHeight < threshold
    shouldScrollToBottomRef.current = atBottom

    // Show/hide scroll to bottom button based on scroll position
    // Only show if there's meaningful content to scroll to
    const hasSignificantScroll = scrollHeight > clientHeight + 100
    setShowScrollToBottom(!atBottom && hasSignificantScroll)
  }, [])

  // Debounced version of handleScroll
  const debouncedHandleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
    scrollTimeoutRef.current = setTimeout(handleScroll, 16) // ~60fps
  }, [handleScroll])

  // Capture initial message count after hydration and scroll to bottom if there's existing chat history
  useEffect(() => {
    if (hasHydrated && initialMessageCountRef.current === null) {
      initialMessageCountRef.current = messagesToRender.length

      // If there are existing messages on initial load, scroll to bottom
      if (messagesToRender.length > 0) {
        // Use requestAnimationFrame to ensure all content is rendered before scrolling
        requestAnimationFrame(() => {
          updateSpacerHeight()
          // Scroll to bottom when reloading a session with chat history
          scrollToBottom(true) // Use snap scroll for instant positioning
        })
      } else {
        // No existing messages, just update spacer height
        requestAnimationFrame(() => {
          updateSpacerHeight()
        })
      }
    }
  }, [hasHydrated, messagesToRender.length, updateSpacerHeight, scrollToBottom])

  // Invalidate cache on rollback (when message count decreases)
  const previousMessageCountRef = useRef<number>(0)
  useEffect(() => {
    const currentMessageCount = messagesToRender.length
    const previousMessageCount = previousMessageCountRef.current

    // If message count decreased, it's likely a rollback - clear the cache
    if (previousMessageCount > 0 && currentMessageCount < previousMessageCount) {
      console.log('[ChatDisplay] Rollback detected, clearing block size cache', {
        previousCount: previousMessageCount,
        currentCount: currentMessageCount,
        cacheSize: blockSizeCacheRef.current.size,
      })

      blockSizeCacheRef.current.clear()
      setAverageBlockSize(400) // Reset to default

      // Trigger remeasure after cache clear
      requestAnimationFrame(() => virtualizer.measure())
    }

    previousMessageCountRef.current = currentMessageCount
  }, [messagesToRender.length, virtualizer])

  // Invalidate cache on session change (switching conversations)
  const previousSessionIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (previousSessionIdRef.current !== null && previousSessionIdRef.current !== sessionId) {
      console.log('[ChatDisplay] Session changed, clearing block size cache', {
        previousSessionId: previousSessionIdRef.current,
        currentSessionId: sessionId,
        cacheSize: blockSizeCacheRef.current.size,
      })

      blockSizeCacheRef.current.clear()
      setAverageBlockSize(400) // Reset to default

      // Trigger remeasure after cache clear
      requestAnimationFrame(() => virtualizer.measure())
    }

    previousSessionIdRef.current = sessionId
  }, [sessionId, virtualizer])

  // Update spacer when activeTypewriterMessageId changes (start/stop of typewriter, no auto-scroll)
  useEffect(() => {
    if (activeTypewriterMessageId) {
      // When typewriter starts, update spacer but don't auto-scroll
      updateSpacerHeight()
    } else {
      // When typewriter completes (activeTypewriterMessageId becomes null),
      // update spacer after DOM settles to eliminate unnecessary bottom white space
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateSpacerHeight()
        })
      })
    }
  }, [activeTypewriterMessageId, updateSpacerHeight])

  // Set up ResizeObserver to monitor chat container changes
  useEffect(() => {
    if (!chatListRef.current) return

    const chatContainer = chatListRef.current

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === chatContainer) {
          // Update spacer height when container size changes (no auto-scroll)
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            updateSpacerHeight()
          })
        }
      }
    })

    resizeObserverRef.current.observe(chatContainer)

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
    }
  }, [updateSpacerHeight])

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    scrollToOptimalPosition,
    updateSpacerHeight,
    scrollToBottom,
    getScrollContainer: () => chatListRef.current,
  }))

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  // Ensure we re-measure when block composition changes (e.g., thinking animation add/remove)
  useEffect(() => {
    requestAnimationFrame(() => virtualizer.measure())
  }, [blocksToRender.length, showThinkingAnimation, virtualizer])

  return (
    <div className={cn('relative flex h-full w-full flex-col', className)}>
      {/* Messages area */}
      <section
        className="scrollbar-auto max-h-full min-h-0 w-full flex-1 overflow-y-auto px-6 pt-12"
        aria-label="Chat messages"
        ref={chatListRef}
        tabIndex={0}
        aria-live="polite"
        role="log"
        onScroll={debouncedHandleScroll}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualBlocks.map((virtualBlock) => {
            const block = blocksToRender[virtualBlock.index]
            return (
              <div
                key={virtualBlock.key}
                data-index={virtualBlock.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualBlock.start}px)`,
                  minHeight: 'auto',
                  overflow: 'visible', // Allow content to expand naturally
                }}
                className="w-full"
                id={`block-${block.id}`}
              >
                {block.messages.map((message, messageIndex) => {
                  // Check if we should show divider above this message
                  // Only show for assistant messages that are the first in a sequence
                  const showDividerAbove =
                    message.role === 'assistant' &&
                    messageIndex > 0 &&
                    (() => {
                      const prevMessage = block.messages[messageIndex - 1]
                      // Show divider if the previous message was also from assistant
                      return prevMessage?.role === 'assistant'
                    })()

                  // Check if this message should get tighter spacing
                  const useTightSpacing = shouldUseTightSpacing(
                    message,
                    block.messages[messageIndex - 1]
                  )

                  // Only animate messages that are beyond the initial count
                  const shouldAnimateMessage =
                    initialMessageCountRef.current === null ||
                    messagesToRender.findIndex((m) => m.id === message.id) >=
                      initialMessageCountRef.current

                  return (
                    <div
                      key={message.id}
                      className={`w-full ${useTightSpacing ? 'pt-1' : 'pt-3'}`}
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {message.role === 'system' && message.id === 'thinking-animation' ? (
                        // Render thinking animation
                        <div className="flex w-full gap-x-1">
                          <div className="flex grow flex-col gap-y-2 overflow-hidden">
                            <div className="h-10 w-20">
                              <RiveComponent
                                src="/animation/final-thinking-animation.riv"
                                stateMachines={'State Machine 1'}
                                layout={new Layout({ fit: Fit.FitHeight })}
                                onLoad={() => console.log('Rive animation loaded successfully')}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <ChatMessageItem
                          chatMessage={message}
                          showDividerAbove={showDividerAbove}
                          shouldAnimate={shouldAnimateMessage}
                          onContentChange={triggerSpacerUpdate}
                          useTightSpacing={useTightSpacing}
                        />
                      )}
                    </div>
                  )
                })}

                {/* Add invisible spacer div to newest block to fill remaining height */}
                {block.isNewest && (
                  <div
                    style={{ height: `${defaultSpacerHeight}px` }}
                    className="w-full"
                    aria-hidden="true"
                  />
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* Messages area bottom padding for scroll button */}
      <div className="relative flex w-full justify-center">
        {/* Scroll to bottom button */}
        <AnimatePresence>
          {showScrollToBottom && (
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute bottom-8"
            >
              <button
                onClick={() => scrollToBottom()}
                className="flex items-center justify-center rounded-full bg-[#F3F3F3] px-4 py-1 text-gray-400 shadow-lg transition-colors duration-200 hover:bg-[#E0E0E0]"
                aria-label="Scroll to bottom"
              >
                <FiChevronDown className="h-4 w-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
})

export default ChatDisplay
